/**
 * A table cell as the document stores it: a plain string, or an object
 * `{ content | text, colspan?, rowspan? }` — the shape imports write and every
 * merged cell uses. The LaTeX and Typst exporters read it by exactly these
 * rules (TableCellText / TableCellSpan); the editor must too, or a cell the PDF
 * prints shows blank on screen.
 */
export type TableCell = string | { content?: string; text?: string; colspan?: number; rowspan?: number; [k: string]: unknown };

export function cellText(cell: unknown): string {
  if (typeof cell === "string") return cell;
  if (cell && typeof cell === "object") {
    const c = cell as { content?: unknown; text?: unknown };
    if (typeof c.content === "string") return c.content;
    if (typeof c.text === "string") return c.text;
  }
  return "";
}

export function cellSpan(cell: unknown): { colspan: number; rowspan: number } {
  if (!cell || typeof cell !== "object") return { colspan: 1, rowspan: 1 };
  const c = cell as { colspan?: unknown; rowspan?: unknown };
  const n = (v: unknown) => (typeof v === "number" && v > 1 ? Math.floor(v) : 1);
  return { colspan: n(c.colspan), rowspan: n(c.rowspan) };
}

/**
 * The cell with new text, keeping everything else it carries. Editing a merged
 * cell's text must not turn it into a plain string — that drops its span.
 */
export function withText(cell: unknown, text: string): TableCell {
  if (cell && typeof cell === "object") {
    const c = cell as Record<string, unknown>;
    return typeof c.text === "string" && typeof c.content !== "string"
      ? { ...c, text }
      : { ...c, content: text };
  }
  return text;
}

/**
 * Grid positions a span absorbs. The editor keeps full-width rows, so an
 * absorbed cell is still in the data; like `\multicolumn`, it is not drawn.
 */
export function coveredCells(rows: readonly (readonly unknown[])[]): Set<string> {
  const covered = new Set<string>();
  rows.forEach((row, r) =>
    row.forEach((cell, c) => {
      const { colspan, rowspan } = cellSpan(cell);
      for (let rr = r; rr < r + rowspan; rr++)
        for (let cc = c; cc < c + colspan; cc++)
          if (rr !== r || cc !== c) covered.add(`${rr}:${cc}`);
    }),
  );
  return covered;
}
